import { useMemo } from 'react';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  type CanCancelOrderQueryVariables,
  useCanCancelOrderQuery,
} from '../../graphql/OrderStatus.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A custom hook that returns a boolean indicating whether the given order
 * can be canceled and a helper to refetch the status.
 * Based on the feature flag value, it uses two different sources of information
 * to determine cancellation state:
 *
 * - `order.canCancel` field (non-reliable source that uses just the order status)
 * - `canCancel` query (reliable source that uses order real-time state
 *    and other factors to determine whether it can be canceled)
 */
export const useCanCancelOrder = (order: OrderToCancel | undefined) => {
  const { customer } = useCustomer();
  const isInAppOrderCancellationEnabled = useFeatureFlag(
    'CELS-1476-in-app-order-cancellation',
  );
  const isLoggedIn = useIsLoggedIn();

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const canCancelOrderVariables = useMemo(() => {
    if (!order || !customer.id) return;

    const variables: CanCancelOrderQueryVariables = {
      input: {
        customerId: customer.id,
        orderId: order.id,
        restaurantId: order.restaurant.id,
        wantedTime: order.wantedTime,
        origin: 'customer',
      },
    };

    return variables;
  }, [customer.id, order]);

  // ─── Query ───────────────────────────────────────────────────────────────────

  const shouldPauseCanCancelQuery =
    !isInAppOrderCancellationEnabled || !isLoggedIn || !canCancelOrderVariables;

  const [{ data }, refetchCanCancelStatus] = useCanCancelOrderQuery({
    variables: canCancelOrderVariables,
    pause: shouldPauseCanCancelQuery,
    requestPolicy: 'network-only',
  });
  const resolvedData = data?.canCancel?.[0];

  // ─────────────────────────────────────────────────────────────────────────────

  // the new more reliable data source
  if (isInAppOrderCancellationEnabled)
    return {
      canCancel:
        resolvedData?.__typename === 'CanCancel' && resolvedData.canCancel,
      refetchCanCancelStatus,
    };

  // the deprecated unreliable data source
  return {
    canCancel: Boolean(order?.canCancel),
    refetchCanCancelStatus: () => undefined,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderToCancel = Readonly<{
  id: string;
  canCancel: boolean;
  restaurant: {
    id: string;
  };
  wantedTime: string; // ISO
}>;
